import * as React from 'react';
import { ActivityIndicator, Image, Text, View, useWindowDimensions } from 'react-native';

import { getLocalizedTexts } from '../../Locales';
import color from '../../style/color';
import {
    UrlStep,
    InstallationStep,
    useLogEvents,
    useInstallationStep,
    useCloseObsoletePagesWhenSecondWaitingPageIsOpened,
    checkIsOsVersionMajorGreaterOrEqual,
    logVerticalInstructionScreensAbTestGroup,
} from '../../lib/mobileSafariExtension/mobileSafariExtension';
import {
    InstructionActivateJokoExtension,
    InstructionAllowAlways,
    InstructionAllowOnAllWebsites,
    InstructionClickBanner,
    InstructionClickExtensionWarningIcon,
    InstructionClickSafariSettings,
    InstructionManageExtensions,
    InstructionSystemSettingsAllow,
    InstructionSystemSettingsOtherWebsites,
    InstructionSystemSettingsClickButton,
} from '../../components/onboarding/mobileSafariExtension/Instructions';
import {
    containerStyleWithGap,
    getImageActivationPreambleStyle,
    newDesignSystemFontStyles,
    styles,
} from '../../components/onboarding/mobileSafariExtension/Styles';
import {
    NewDesignSystemButton,
    MissingVerifyBannerButton,
    TosConsentButton,
    onPressReturnToApp,
    onPressOpenSystemSettings,
    onPressStartActivation,
} from '../../components/onboarding/mobileSafariExtension/Buttons';
import { Header } from '../../components/onboarding/mobileSafariExtension/Header';
import { getRegion, localizeAsset, LocalizedAsset, Region } from '../../lib/auth/region';
import { InlineMp4 } from '../../components/onboarding/mobileSafariExtension/InlineMp4';
import {
    VerticalInstructions,
    VerticalInstructionStep,
    verticalActivationIllustrations,
    verticalAuthorizationFallbackIllustrations,
} from '../../components/onboarding/mobileSafariExtension/VerticalInstructions';
import {
    getDoesBelongToTestGroupPreRegistration,
    PreRegistrationTwoGroupAbTestSettingsKey,
} from '../../lib/mobileSafariExtension/preRegistrationUserSeed';

const widgetIllustrationActivationPreamble: LocalizedAsset = {
    [Region.FR]: { uri: '/assets/images/illustrations/mobile-safari-extension-installation/widget-offer.png' },
    [Region.US]: { uri: '/assets/images/illustrations/mobile-safari-extension-installation/widget-offer-us.png' },
    [Region.GB]: { uri: '/assets/images/illustrations/mobile-safari-extension-installation/widget-offer-us.png' },
};
const activationAnimation = {
    [Region.FR]:
        '/assets/images/illustrations/mobile-safari-extension-installation/MSE animations_flow 1_activation BIG_longer_FR_aA.mp4',
    [Region.US]:
        '/assets/images/illustrations/mobile-safari-extension-installation/MSE animations_flow 1_activation BIG_longer_US_aA.mp4',
    [Region.GB]:
        '/assets/images/illustrations/mobile-safari-extension-installation/MSE animations_flow 1_activation BIG_longer_US_aA.mp4',
};
const activationAnimationIos18 = {
    [Region.FR]:
        '/assets/images/illustrations/mobile-safari-extension-installation/MSE_animations_flow_1_activation_BIG_longer_FR_1.mp4',
    [Region.US]:
        '/assets/images/illustrations/mobile-safari-extension-installation/MSE_animations_flow_1_activation_BIG_longer_US_1.mp4',
    [Region.GB]:
        '/assets/images/illustrations/mobile-safari-extension-installation/MSE_animations_flow_1_activation_BIG_longer_US_1.mp4',
};
const activationAnimationPlaceholder =
    '/assets/images/illustrations/mobile-safari-extension-installation/activation-animation-placeholder.png';
const activationAnimationPlaceholderIos18 = {
    [Region.FR]:
        '/assets/images/illustrations/mobile-safari-extension-installation/activation-animation-placeholder-ios-18.png',
    [Region.US]:
        '/assets/images/illustrations/mobile-safari-extension-installation/activation-animation-placeholder-ios-18-us.png',
    [Region.GB]:
        '/assets/images/illustrations/mobile-safari-extension-installation/activation-animation-placeholder-ios-18-us.png',
};
const authorizationWithBannerAnimation = {
    [Region.FR]:
        '/assets/images/illustrations/mobile-safari-extension-installation/MSE animations_flow 2_authorisation with banner BIG_longer_FR.mp4',
    [Region.US]:
        '/assets/images/illustrations/mobile-safari-extension-installation/MSE animations_flow 2_authorisation with banner BIG_longer_US.mp4',
    [Region.GB]:
        '/assets/images/illustrations/mobile-safari-extension-installation/MSE animations_flow 2_authorisation with banner BIG_longer_US.mp4',
};
const authorizationWithBannerAnimationPlaceholder = {
    [Region.FR]:
        '/assets/images/illustrations/mobile-safari-extension-installation/authorization-with-banner-animation-placeholder.png',
    [Region.US]:
        '/assets/images/illustrations/mobile-safari-extension-installation/authorization-with-banner-animation-placeholder-us.png',
    [Region.GB]:
        '/assets/images/illustrations/mobile-safari-extension-installation/authorization-with-banner-animation-placeholder-us.png',
};
const authorizationFallbackAnimation = {
    [Region.FR]:
        '/assets/images/illustrations/mobile-safari-extension-installation/MSE animations_flow 3_authorisation with aA BIG_longer_FR.mp4',
    [Region.US]:
        '/assets/images/illustrations/mobile-safari-extension-installation/MSE animations_flow 3_authorisation with aA BIG_longer_US.mp4',
    [Region.GB]:
        '/assets/images/illustrations/mobile-safari-extension-installation/MSE animations_flow 3_authorisation with aA BIG_longer_US.mp4',
};
const authorizationFallbackAnimationIos18 = {
    [Region.FR]:
        '/assets/images/illustrations/mobile-safari-extension-installation/MSE animations_flow 3_authorisation with aA BIG_longer_FR_EQUAL.mp4',
    [Region.US]:
        '/assets/images/illustrations/mobile-safari-extension-installation/MSE animations_flow 3_authorisation with aA BIG_longer_US_EQUAL.mp4',
    [Region.GB]:
        '/assets/images/illustrations/mobile-safari-extension-installation/MSE animations_flow 3_authorisation with aA BIG_longer_US_EQUAL.mp4',
};
const authorizationFallbackAnimationPlaceholder =
    '/assets/images/illustrations/mobile-safari-extension-installation/authorization-fallback-animation-placeholder.png';
const authorizationFallbackAnimationPlaceholderIos18 = {
    [Region.FR]:
        '/assets/images/illustrations/mobile-safari-extension-installation/authorization-fallback-animation-placeholder-ios-18.png',
    [Region.US]:
        '/assets/images/illustrations/mobile-safari-extension-installation/authorization-fallback-animation-placeholder-ios-18-us.png',
    [Region.GB]:
        '/assets/images/illustrations/mobile-safari-extension-installation/authorization-fallback-animation-placeholder-ios-18-us.png',
};
// Region-specific Amazon illustration for the conclusion (success) screen (FR: amazon.fr, US: amazon.com).
const successIllustration: LocalizedAsset = {
    [Region.FR]: {
        uri: '/assets/images/illustrations/mobile-safari-extension-installation/success-illustration-amazon-fr.png',
    },
    [Region.US]: {
        uri: '/assets/images/illustrations/mobile-safari-extension-installation/success-illustration-amazon-us.png',
    },
    [Region.GB]: {
        uri: '/assets/images/illustrations/mobile-safari-extension-installation/success-illustration-amazon-us.png',
    },
};
const tickCircleIcon = { uri: '/assets/images/icons/tick-circle.svg' };

export function MobileSafariExtensionInstallationPageContentRevamped({
    urlStep,
    isDevStackMode,
    userId,
    userSeed,
    deviceId,
    isOnboarding,
    isBeforeOnboarding,
}: {
    urlStep: UrlStep;
    isDevStackMode: boolean;
    userId: string | undefined;
    userSeed: number | undefined;
    deviceId: string | undefined;
    isOnboarding: boolean;
    isBeforeOnboarding: boolean;
}) {
    const shouldDisplayStepper = useShouldDisplayStepperInRevampedFlow(userSeed);
    const shouldUseVerticalInstructionScreens = useShouldUseVerticalInstructionScreensInRevampedFlow({
        userSeed,
        userId,
        deviceId,
        isDevStackMode,
    });
    const [step, setStep] = useInstallationStep({
        urlStep,
        userId,
        isDevStackMode,
        shouldSkipActivationPreamble: !isBeforeOnboarding,
        shouldSkipAuthorizationPreamble: true,
    });
    useLogEvents({
        urlStep,
        userId,
        deviceId,
        isDevStackMode,
        isOnboarding,
        isBeforeOnboarding,
        isFlowWithSystemSettings: false,
        isRevampedOnboardingFlow: true,
    });
    useCloseObsoletePagesWhenSecondWaitingPageIsOpened({
        step,
        userId,
        deviceId,
        isDevStackMode,
        isOnboarding,
        isBeforeOnboarding,
        isRevampedOnboardingFlow: true,
    });
    if (!step || step === InstallationStep.firstWaitingStep || step === InstallationStep.secondWaitingStep)
        return <WaitingScreen />;
    switch (step) {
        case InstallationStep.activateStepPreamble:
            return (
                <ActivationPreamble {...{ isDevStackMode, userId, deviceId, isOnboarding, isBeforeOnboarding, setStep }} />
            );
        case InstallationStep.activateStep:
            return <Activation {...{ shouldDisplayStepper, shouldUseVerticalInstructionScreens }} />;
        case InstallationStep.authorizeWithBannerStep:
            return (
                <AuthorizationWithBanner
                    {...{
                        step,
                        setStep,
                        userId,
                        deviceId,
                        isDevStackMode,
                        shouldDisplayStepper,
                    }}
                />
            );
        case InstallationStep.authorizeStep:
            return <AuthorizationFallback {...{ shouldDisplayStepper, shouldUseVerticalInstructionScreens }} />;
        case InstallationStep.missingAllWebsitesAuthorization:
            return <MissingAllWebsitesAuthorization {...{ shouldDisplayStepper }} />;
        case InstallationStep.missingAllWebsitesAuthorizationAfterRetry:
            return (
                <MissingAllWebsitesAuthorizationAfterRetry {...{ userId, deviceId, isDevStackMode, shouldDisplayStepper }} />
            );
        case InstallationStep.conclusionStep:
            return <Conclusion {...{ userId, deviceId }} />;
        default:
            throw new Error(`Invalid step: ${step}`);
    }
}

function WaitingScreen() {
    return (
        <View style={styles.containerWaitingScreen}>
            <ActivityIndicator size="large" color={color.alto} />
        </View>
    );
}

function ActivationPreamble({
    isDevStackMode,
    userId,
    deviceId,
    isOnboarding,
    isBeforeOnboarding,
    setStep,
}: {
    isDevStackMode: boolean;
    userId: string | undefined;
    deviceId: string | undefined;
    isOnboarding: boolean;
    isBeforeOnboarding: boolean;
    setStep: (step: InstallationStep) => void;
}) {
    const texts = getLocalizedTexts().mobileSafariExtensionInstallationPagesRevamped;
    const [isTosConsentNeeded, setIsTosConsentNeeded] = React.useState<boolean>(isBeforeOnboarding);
    const region = getRegion();
    return (
        <View style={containerStyleWithGap(40)}>
            <Header shouldDisplayStepper={false} step={InstallationStep.activateStepPreamble} />
            <View style={styles.containerGreenCircleBackground}>
                <Image
                    source={localizeAsset(widgetIllustrationActivationPreamble)}
                    style={getImageActivationPreambleStyle(region)}
                />
            </View>
            <View style={styles.containerButtons}>
                <TosConsentButton
                    {...{
                        isBeforeOnboarding,
                        isTosConsentNeeded,
                        setIsTosConsentNeeded,
                        isDevStackMode,
                        userId,
                        deviceId,
                    }}
                />
                <View style={{ marginTop: 16 }}>
                    <NewDesignSystemButton
                        onPress={onPressStartActivation({
                            isDevStackMode,
                            userId,
                            deviceId,
                            isOnboarding,
                            isBeforeOnboarding,
                            setStep,
                        })}
                        disabled={isTosConsentNeeded}
                        textStyle={newDesignSystemFontStyles.actionMedium}>
                        {texts.activateStep.preamble.startButton}
                    </NewDesignSystemButton>
                </View>
            </View>
        </View>
    );
}

function Activation({
    shouldDisplayStepper,
    shouldUseVerticalInstructionScreens,
}: {
    shouldDisplayStepper: boolean | undefined;
    shouldUseVerticalInstructionScreens: boolean | undefined;
}) {
    const region = getRegion();
    if (shouldUseVerticalInstructionScreens === undefined) return <WaitingScreen />;
    return (
        <View style={containerStyleWithGap(24)}>
            <Header
                shouldDisplayStepper={shouldDisplayStepper}
                shouldUseVerticalStepper={shouldUseVerticalInstructionScreens}
                step={InstallationStep.activateStep}
            />
            {shouldUseVerticalInstructionScreens ? (
                <VerticalInstructions>
                    <VerticalInstructionStep illustration={verticalActivationIllustrations[1]}>
                        <InstructionClickSafariSettings itemNumber={1} />
                    </VerticalInstructionStep>
                    <VerticalInstructionStep illustration={verticalActivationIllustrations[2]}>
                        <InstructionManageExtensions itemNumber={2} />
                    </VerticalInstructionStep>
                    <VerticalInstructionStep illustration={verticalActivationIllustrations[3]}>
                        <InstructionActivateJokoExtension itemNumber={3} />
                    </VerticalInstructionStep>
                </VerticalInstructions>
            ) : (
                <>
                    <View style={styles.containerInstructions}>
                        <InstructionClickSafariSettings itemNumber={1} />
                        <InstructionManageExtensions itemNumber={2} />
                        <InstructionActivateJokoExtension itemNumber={3} />
                    </View>
                    {checkIsOsVersionMajorGreaterOrEqual(18) ? (
                        <InlineMp4
                            src={activationAnimationIos18[region]}
                            poster={activationAnimationPlaceholderIos18[region]}
                        />
                    ) : (
                        <InlineMp4 src={activationAnimation[region]} poster={activationAnimationPlaceholder} />
                    )}
                </>
            )}
        </View>
    );
}

// The banner-entry authorization screen is excluded from the vertical-instruction A/B test (GRW-252):
// the new design covers only the standard activation + aA fallback authorization screens, and this
// banner flow is currently disabled (0% proportion). It always renders the control (animated) layout.
function AuthorizationWithBanner({
    setStep,
    userId,
    deviceId,
    isDevStackMode,
    shouldDisplayStepper,
}: {
    setStep: (step: InstallationStep) => void;
    userId?: string;
    deviceId?: string;
    isDevStackMode?: boolean;
    shouldDisplayStepper: boolean | undefined;
}) {
    const region = getRegion();
    return (
        <View style={containerStyleWithGap(24)}>
            <Header shouldDisplayStepper={shouldDisplayStepper} step={InstallationStep.authorizeWithBannerStep} />
            <View style={styles.containerInstructionsWithMissingBannerButton}>
                <View style={styles.containerInstructions}>
                    <InstructionClickBanner itemNumber={1} />
                    <InstructionAllowAlways itemNumber={2} />
                    <InstructionAllowOnAllWebsites itemNumber={3} />
                </View>
                <MissingVerifyBannerButton {...{ setStep, userId, deviceId, isDevStackMode }} />
            </View>
            <InlineMp4
                src={authorizationWithBannerAnimation[region]}
                poster={authorizationWithBannerAnimationPlaceholder[region]}
            />
        </View>
    );
}

function AuthorizationFallback({
    shouldDisplayStepper,
    shouldUseVerticalInstructionScreens,
}: {
    shouldDisplayStepper: boolean | undefined;
    shouldUseVerticalInstructionScreens: boolean | undefined;
}) {
    const region = getRegion();
    if (shouldUseVerticalInstructionScreens === undefined) return <WaitingScreen />;
    return (
        <View style={containerStyleWithGap(24)}>
            <Header
                shouldDisplayStepper={shouldDisplayStepper}
                shouldUseVerticalStepper={shouldUseVerticalInstructionScreens}
                step={InstallationStep.authorizeStep}
            />
            {shouldUseVerticalInstructionScreens ? (
                <VerticalInstructions>
                    <VerticalInstructionStep illustration={verticalAuthorizationFallbackIllustrations[1]}>
                        <InstructionClickSafariSettings itemNumber={1} />
                    </VerticalInstructionStep>
                    <VerticalInstructionStep illustration={verticalAuthorizationFallbackIllustrations[2]}>
                        <InstructionClickExtensionWarningIcon itemNumber={2} />
                    </VerticalInstructionStep>
                    <VerticalInstructionStep illustration={verticalAuthorizationFallbackIllustrations[3]} hasFullTopPadding>
                        <InstructionAllowAlways itemNumber={3} />
                    </VerticalInstructionStep>
                    <VerticalInstructionStep illustration={verticalAuthorizationFallbackIllustrations[4]} hasFullTopPadding>
                        <InstructionAllowOnAllWebsites itemNumber={4} />
                    </VerticalInstructionStep>
                </VerticalInstructions>
            ) : (
                <>
                    <View style={styles.containerInstructions}>
                        <InstructionClickSafariSettings itemNumber={1} />
                        <InstructionClickExtensionWarningIcon itemNumber={2} />
                        <InstructionAllowAlways itemNumber={3} />
                        <InstructionAllowOnAllWebsites itemNumber={4} />
                    </View>
                    {checkIsOsVersionMajorGreaterOrEqual(18) ? (
                        <InlineMp4
                            src={authorizationFallbackAnimationIos18[region]}
                            poster={authorizationFallbackAnimationPlaceholderIos18[region]}
                        />
                    ) : (
                        <InlineMp4
                            src={authorizationFallbackAnimation[region]}
                            poster={authorizationFallbackAnimationPlaceholder}
                        />
                    )}
                </>
            )}
        </View>
    );
}

function MissingAllWebsitesAuthorization({ shouldDisplayStepper }: { shouldDisplayStepper: boolean | undefined }) {
    const region = getRegion();
    return (
        <View style={containerStyleWithGap(24)}>
            <Header shouldDisplayStepper={shouldDisplayStepper} step={InstallationStep.missingAllWebsitesAuthorization} />
            <View style={styles.containerSplitInstructions}>
                <View style={styles.containerInstructions}>
                    <InstructionClickSafariSettings itemNumber={1} />
                    <InstructionClickExtensionWarningIcon itemNumber={2} />
                </View>
                <View style={[styles.containerInstructions, { backgroundColor: color.pippin }]}>
                    <InstructionAllowAlways itemNumber={3} />
                    <InstructionAllowOnAllWebsites itemNumber={4} />
                </View>
            </View>
            {checkIsOsVersionMajorGreaterOrEqual(18) ? (
                <InlineMp4
                    src={authorizationFallbackAnimationIos18[region]}
                    poster={authorizationFallbackAnimationPlaceholderIos18[region]}
                />
            ) : (
                <InlineMp4 src={authorizationFallbackAnimation[region]} poster={authorizationFallbackAnimationPlaceholder} />
            )}
        </View>
    );
}

function MissingAllWebsitesAuthorizationAfterRetry({
    userId,
    deviceId,
    isDevStackMode,
    shouldDisplayStepper,
}: {
    userId: string | undefined;
    deviceId: string | undefined;
    isDevStackMode: boolean;
    shouldDisplayStepper: boolean | undefined;
}) {
    const texts = getLocalizedTexts().mobileSafariExtensionInstallationPagesRevamped;
    return (
        <View style={containerStyleWithGap(24)}>
            <Header
                shouldDisplayStepper={shouldDisplayStepper}
                step={InstallationStep.missingAllWebsitesAuthorizationAfterRetry}
            />
            <View style={styles.body}>
                <View style={styles.containerInstructions}>
                    <InstructionSystemSettingsClickButton itemNumber={1} />
                    <InstructionSystemSettingsOtherWebsites itemNumber={2} />
                    <InstructionSystemSettingsAllow itemNumber={3} />
                </View>
                <NewDesignSystemButton
                    onPress={onPressOpenSystemSettings({
                        userId,
                        deviceId,
                        isDevStackMode,
                        from: 'missingAllWebsitesAuthorizationAfterRetryPage',
                    })}
                    textStyle={newDesignSystemFontStyles.actionMedium}>
                    {texts.missingAuthorization.systemSettingsButton}
                </NewDesignSystemButton>
            </View>
        </View>
    );
}

// Joko side-button geometry as a % of the illustration. The card matches the image aspect ratio,
// so these percentages map directly onto the button. JOKO_BUTTON_RIGHT is the distance from the
// card's right edge to the button's right edge — the pulse is pinned there so it never grows right.
const JOKO_BUTTON_CENTER = { left: '77%', top: '71.6%' };
const JOKO_BUTTON_RIGHT = '18.66%';

function Conclusion({ userId, deviceId }: { userId: string | undefined; deviceId: string | undefined }) {
    const texts = getLocalizedTexts().mobileSafariExtensionInstallationPagesRevamped;
    // Scale the pulse to the card width so it stays proportional to the button on every screen size.
    // Card width = window width minus the 24px horizontal padding on each side; 342 ≈ the design width.
    const { width } = useWindowDimensions();
    const jokoPulseScale = (width - 48) / 342;
    return (
        // paddingBottom reserves the fixed CTA's footprint (52px button + 24px bottom offset + gap)
        // so the illustration card is never hidden behind it.
        <View style={[containerStyleWithGap(40), { paddingBottom: 100 }]}>
            <Header
                shouldDisplayStepper={false}
                step={InstallationStep.conclusionStep}
                titleStyle={newDesignSystemFontStyles.headlineMedium}
            />
            <View style={styles.conclusionIllustrationContainer}>
                <Image source={localizeAsset(successIllustration)} style={styles.conclusionIllustration} />
                {/* Pulsating outline behind the Joko side-button (raw div for the joko-pulse @keyframes in
                    index.css). Pinned to the button's right edge and centered vertically, so it grows only
                    left/top/bottom (not into the phone frame). The button SVG below masks the inner part. */}
                <div
                    style={
                        {
                            position: 'absolute',
                            right: JOKO_BUTTON_RIGHT,
                            top: JOKO_BUTTON_CENTER.top,
                            '--joko-pulse-scale': jokoPulseScale,
                            width: 'calc(var(--joko-pulse-scale) * 31px)',
                            height: 'calc(var(--joko-pulse-scale) * 31px)',
                            borderRadius:
                                'calc(var(--joko-pulse-scale) * 9px) 0 0 calc(var(--joko-pulse-scale) * 9px)',
                            background: 'rgba(80, 241, 115, 0.3)',
                            transform: 'translateY(-50%)',
                            animation: 'joko-pulse 1100ms infinite',
                            pointerEvents: 'none',
                        } as React.CSSProperties
                    }
                />
                {/* The Joko button re-drawn on top of the outline (matches the one baked into the image),
                    masking the pulse so it stays outside the button. Sized to the button's bbox (% of card). */}
                <img
                    src="/assets/images/logos/joko-side-button.svg"
                    alt=""
                    style={{
                        position: 'absolute',
                        left: JOKO_BUTTON_CENTER.left,
                        top: JOKO_BUTTON_CENTER.top,
                        width: '8.75%',
                        height: '9.53%',
                        transform: 'translate(-50%, -50%)',
                        pointerEvents: 'none',
                    }}
                />
            </View>
            {/* Raw div so we can use position: fixed (react-native's style types omit it); keeps the CTA
                pinned to the viewport bottom while the page scrolls. flex column makes the button full-width. */}
            <div style={{ position: 'fixed', left: 24, right: 24, bottom: 24, display: 'flex', flexDirection: 'column' }}>
                <NewDesignSystemButton
                    onPress={onPressReturnToApp({ userId, deviceId })}
                    textStyle={newDesignSystemFontStyles.actionMedium}>
                    {texts.conclusion.returnToJokoButton}
                </NewDesignSystemButton>
            </div>
        </View>
    );
}

export function Stepper({ currentStep }: { currentStep: number }) {
    const texts = getLocalizedTexts().mobileSafariExtensionInstallationPagesRevamped.header;
    return (
        <View style={styles.containerStepper}>
            <Step
                isCompleted={currentStep > 0}
                isCurrentStep={currentStep === 0}
                label={texts.left}
                isFirstStep={true}
                isLastStep={false}
            />
            <Step
                isCompleted={currentStep > 1}
                isCurrentStep={currentStep === 1}
                label={texts.right}
                isFirstStep={false}
                isLastStep={true}
            />
        </View>
    );
}

// "Version B" (GRW-252) stepper used by the vertical instruction screens: the active section is a black
// rounded pill with white text, the inactive section is greyed-out text, joined by a thin connector line.
export function VerticalStepper({ currentStep }: { currentStep: number }) {
    const texts = getLocalizedTexts().mobileSafariExtensionInstallationPagesRevamped.header;
    return (
        <View style={styles.containerVerticalStepper}>
            <VerticalStepperLabel label={texts.left} isActive={currentStep === 0} />
            <View style={styles.verticalStepperConnector} />
            <VerticalStepperLabel label={texts.right} isActive={currentStep === 1} />
        </View>
    );
}

function VerticalStepperLabel({ label, isActive }: { label: string; isActive: boolean }) {
    return (
        <View style={[styles.verticalStepperLabel, isActive ? styles.verticalStepperLabelActive : undefined]}>
            <Text style={[styles.verticalStepperLabelText, isActive ? styles.verticalStepperLabelTextActive : undefined]}>
                {label}
            </Text>
        </View>
    );
}

function Step({
    isCompleted,
    isCurrentStep,
    label,
    isFirstStep,
    isLastStep,
}: {
    isCompleted: boolean;
    isCurrentStep: boolean;
    label: string;
    isFirstStep: boolean;
    isLastStep: boolean;
}) {
    return (
        <View style={styles.containerStep}>
            <View style={[styles.containerStepTop, !isCompleted ? { marginTop: 4 } : undefined]}>
                {isFirstStep ? (
                    <View style={[styles.stepLine, { height: 0 }]} />
                ) : (
                    <View
                        style={[
                            styles.stepLine,
                            !isCurrentStep && !isCompleted ? { backgroundColor: color.alto } : undefined,
                        ]}
                    />
                )}
                {isCompleted ? (
                    <Image source={tickCircleIcon} style={styles.imageTickCircleIcon} />
                ) : isCurrentStep ? (
                    <View style={styles.currentStep} />
                ) : (
                    <View style={styles.nextStep} />
                )}
                {isLastStep ? (
                    <View style={[styles.stepLine, { height: 0 }]} />
                ) : (
                    <View style={[styles.stepLine, !isCompleted ? { backgroundColor: color.alto } : undefined]} />
                )}
            </View>
            <Text
                style={[
                    styles.textStepLabel,
                    isCurrentStep ? { color: color.black } : undefined,
                    isCompleted ? { marginTop: 4 } : undefined,
                ]}>
                {label}
            </Text>
        </View>
    );
}

export function useShouldDisplayStepperInRevampedFlow(userSeed: number | undefined) {
    const [shouldDisplayStepperInRevampedFlow, setShouldDisplayStepperInRevampedFlow] = React.useState<boolean | undefined>(
        undefined
    );
    React.useEffect(() => {
        async function updateShouldUseRevampedOnboardingFlow() {
            const shouldDisplayStepperInRevampedFlowResult = await getDoesBelongToTestGroupPreRegistration(
                userSeed!,
                PreRegistrationTwoGroupAbTestSettingsKey.shouldDisplayStepperInRevampedFlow
            );
            setShouldDisplayStepperInRevampedFlow(shouldDisplayStepperInRevampedFlowResult);
        }
        updateShouldUseRevampedOnboardingFlow();
    }, []);
    return shouldDisplayStepperInRevampedFlow;
}

export function useShouldUseVerticalInstructionScreensInRevampedFlow({
    userSeed,
    userId,
    deviceId,
    isDevStackMode,
}: {
    userSeed: number | undefined;
    userId: string | undefined;
    deviceId: string | undefined;
    isDevStackMode: boolean;
}) {
    // The test is FR-only: non-FR users (or users without a seed) resolve synchronously to the control group,
    // so they never wait on the settings fetch and are excluded from the experiment.
    const [shouldUseVerticalInstructionScreens, setShouldUseVerticalInstructionScreens] = React.useState<
        boolean | undefined
    >(() => (getRegion() !== Region.FR || !userSeed ? false : undefined));
    React.useEffect(() => {
        if (shouldUseVerticalInstructionScreens !== undefined) return;
        async function updateShouldUseVerticalInstructionScreens() {
            const result = await getDoesBelongToTestGroupPreRegistration(
                userSeed!,
                PreRegistrationTwoGroupAbTestSettingsKey.shouldUseVerticalInstructionScreensInRevampedFlow
            );
            setShouldUseVerticalInstructionScreens(result);
            logVerticalInstructionScreensAbTestGroup({
                userId,
                deviceId,
                isDevStackMode,
                isVerticalInstructionScreensFlow: result,
            });
        }
        updateShouldUseVerticalInstructionScreens();
    }, []);
    return shouldUseVerticalInstructionScreens;
}

export function useShouldShowImprovedWrongBrowserScreen(userSeed: number | undefined) {
    const [shouldShowImprovedWrongBrowserScreen, setShouldShowImprovedWrongBrowserScreen] = React.useState<
        boolean | undefined
    >(undefined);
    React.useEffect(() => {
        async function updateShouldShowImprovedWrongBrowserScreen() {
            const shouldShowImprovedWrongBrowserScreenResult = await getDoesBelongToTestGroupPreRegistration(
                userSeed!,
                PreRegistrationTwoGroupAbTestSettingsKey.shouldShowImprovedWrongBrowserScreen
            );
            setShouldShowImprovedWrongBrowserScreen(shouldShowImprovedWrongBrowserScreenResult);
        }
        updateShouldShowImprovedWrongBrowserScreen();
    }, [userSeed]);
    return shouldShowImprovedWrongBrowserScreen;
}
